// ===========================================
// A reply template's text, with merge-field chips (enquiries plan §12)
// ===========================================
// Shared by Settings → Enquiries (New / Edit template) and the reply box's "Save as template", so
// both insert a field the same way. The insertion rules are the custom email editor's, through
// `utils/mergeFieldInsert` (caret kept, inserted after a selection, never over it, no double insert).

import { useRef } from 'react';
import { Box, TextField } from '@mui/material';
import { REPLY_MERGE_FIELDS, TEMPLATE_BODY_MAX } from '../../../api/enquiry.api';
import MergeFieldChips, { type MergeFieldOption } from '../../common/MergeFieldChips';
import { insertAtSelectionEnd, isDuplicateMouseActivation } from '../../../utils/mergeFieldInsert';

/** One label per field: a field added to `REPLY_MERGE_FIELDS` does not compile until it has one. */
const REPLY_MERGE_FIELD_LABELS: Record<(typeof REPLY_MERGE_FIELDS)[number], string> = {
  parentName: 'Parent name',
  childNames: 'Child names',
  schoolName: 'School name',
  bookingLink: 'Booking link',
};

const REPLY_MERGE_FIELD_OPTIONS: MergeFieldOption[] = REPLY_MERGE_FIELDS.map((field) => ({
  label: REPLY_MERGE_FIELD_LABELS[field],
  placeholder: `{{${field}}}`,
}));

interface ReplyTemplateBodyFieldProps {
  value: string;
  onChange: (value: string) => void;
  helperText: string;
  minRows: number;
  maxRows: number;
}

export default function ReplyTemplateBodyField({ value, onChange, helperText, minRows, maxRows }: ReplyTemplateBodyFieldProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const insertMergeField = (e: React.MouseEvent, placeholder: string) => {
    if (isDuplicateMouseActivation(e)) return;
    e.preventDefault();

    const input = inputRef.current;
    const next = input ? insertAtSelectionEnd(input, placeholder) : { value: value + placeholder, caret: value.length + placeholder.length };
    // `maxLength` stops typing, not a value set from here: a field that would not fit is not added.
    if (next.value.length > TEMPLATE_BODY_MAX) return;
    onChange(next.value);

    if (input) {
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(next.caret, next.caret);
      });
    }
  };

  return (
    <Box>
      <TextField
        label="Reply"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputRef={inputRef}
        multiline
        minRows={minRows}
        maxRows={maxRows}
        fullWidth
        size="small"
        inputProps={{ maxLength: TEMPLATE_BODY_MAX }}
        helperText={helperText}
      />
      <MergeFieldChips fields={REPLY_MERGE_FIELD_OPTIONS} target="reply-template-body" onInsert={insertMergeField} />
    </Box>
  );
}
